import React from "react";
import InnerLayout, { disclosureSidebar } from "../InnerLayout";

const objectives = [
  "Promote resource sharing among libraries.",
  "Provide access to a vast collection of information resources.",
  "Facilitate Inter-Library Loan (ILL) and document delivery services.",
  "Develop and maintain union catalogues and bibliographic databases.",
  "Support research, teaching, and learning through digital resources.",
];

const unionCatalogues = [
  "Union Catalogue of Books",
  "Union Catalogue of Periodicals",
  "Union List of Current Periodicals",
  "Database of Journal Articles",
  "Theses and Dissertations Database",
  "CD-ROM Databases",
];

const benefits = [
  "Access to resources beyond the college library collection.",
  "Availability of rare and specialized books.",
  "Access to scholarly e-journals and e-books.",
  "Faster resource sharing among libraries.",
  "Support for research, projects, dissertations, and publications.",
  "Cost-effective access to information resources.",
];

const majorServices = [
  {
    title: "1. E-Journals Access",
    content:
      "DELNET provides access to thousands of scholarly e-journals through its consortium arrangements. Faculty members, researchers, and students can access peer-reviewed journals covering engineering, science, management, humanities, social sciences, medicine, and other disciplines.",
  },
  {
    title: "2. E-Books Access",
    content:
      "DELNET's Knowledge Gainer Portal provides access to a large collection of e-books in various subjects. Users can search and read digital books for academic and research purposes. Some member institutions report access to millions of e-books through DELNET resources.",
  },
  {
    title: "3. Inter-Library Loan (ILL)",
    content:
      "One of the most valuable services of DELNET is the Inter-Library Loan (ILL) facility.",
    steps: [
      "Search the required book in DELNET's Union Catalogue.",
      "If the book is available in another member library, the librarian places an ILL request.",
      "The lending library sends the book through courier/post.",
      "The user can borrow and use the book through their home library.",
    ],
    note: "This service helps users obtain books that are not available in their own library.",
  },
  {
    title: "4. Document Delivery Service",
    content:
      "If a required journal article, conference paper, thesis, or book chapter is not available locally, DELNET can arrange a scanned copy or photocopy from another member library. This service is especially useful for researchers and Ph.D. scholars.",
  },
  {
    title: "5. Union Catalogues",
    content: "DELNET maintains several union databases, including:",
    list: unionCatalogues,
    note: "These catalogues enable users to identify which library holds a particular resource.",
  },
  {
    title: "6. Discovery Portal",
    content:
      "DELNET offers a single-window search platform where users can search books, journals, articles, theses, dissertations, and other academic resources simultaneously.",
  },
];

const DELNET = () => {
  return (
    <InnerLayout
      title="Developing Library Network (DELNET)"
      image="https://images.pexels.com/photos/12091126/pexels-photo-12091126.jpeg?w=2400"
      breadcrumbs={[
        { label: "Disclosure", to: "/disclosure" },
        { label: "DELNET" },
      ]}
      sidebar={disclosureSidebar}
      sidebarTitle="Disclosure"
    >
      <section className="mb-14">
        <h2
          className="text-3xl md:text-4xl text-brand mb-6 font-semibold"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Digital Library E-Resources
        </h2>

        <div className="bg-white border border-brand/10 p-6">
          <p className="text-[#2a2a2a]/85 leading-relaxed mb-3">
            Digital Library e-resources remote access link:
          </p>
          <a
            href="https://access.vtuconsortium.com/klsvdit"
            target="_blank"
            rel="noreferrer"
            className="text-brand font-semibold hover:underline break-all"
          >
            https://access.vtuconsortium.com/klsvdit
          </a>
        </div>
      </section>

      <section className="mb-14">
        <h2
          className="text-3xl md:text-4xl text-brand mb-6 font-semibold"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          About DELNET
        </h2>

        <div className="vdit-prose">
          <p>
            <strong>DELNET (Developing Library Network)</strong> is one of
            India's largest library networks, established to promote resource
            sharing among libraries. It connects more than 9,700 institutions,
            including universities, colleges, research organizations, medical
            institutions, and public libraries across India and abroad. Its
            motto is "Networking Libraries, Sharing and Spreading Knowledge."
          </p>

          <p>
            <a
              href="https://www.delnet.in/?utm_source=chatgpt.com"
              className="text-brand font-semibold hover:underline"
            >
              DELNET Official Website
            </a>
          </p>
        </div>
      </section>

      <section className="mb-14">
        <h2
          className="text-3xl md:text-4xl text-brand mb-6 font-semibold"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Objectives of DELNET
        </h2>

        <div className="bg-white border border-brand/10 p-6">
          <ul className="space-y-3 text-[#2a2a2a]/85">
            {objectives.map((item) => (
              <li key={item} className="flex gap-3">
                <span className="text-brand">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mb-14">
        <h2
          className="text-3xl md:text-4xl text-brand mb-6 font-semibold"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Major Services Provided by DELNET
        </h2>

        <div className="space-y-5">
          {majorServices.map((service) => (
            <article
              key={service.title}
              className="bg-white border border-brand/10 p-6"
            >
              <h3 className="text-xl text-brand font-semibold mb-3">
                {service.title}
              </h3>

              <p className="text-[#2a2a2a]/85 leading-relaxed">
                {service.content}
              </p>

              {service.steps && (
                <div className="mt-4">
                  <h4 className="text-sm uppercase tracking-widest text-brand font-semibold mb-3">
                    How it works
                  </h4>
                  <ol className="list-decimal pl-5 space-y-2 text-[#2a2a2a]/85">
                    {service.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {service.list && (
                <ul className="mt-4 grid md:grid-cols-2 gap-x-6 gap-y-2 text-[#2a2a2a]/85">
                  {service.list.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="text-brand">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}

              {service.note && (
                <p className="text-[#2a2a2a]/85 leading-relaxed mt-4">
                  {service.note}
                </p>
              )}
            </article>
          ))}
        </div>
      </section>

      <section className="mb-14">
        <h2
          className="text-3xl md:text-4xl text-brand mb-6 font-semibold"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Benefits to Library Users
        </h2>

        <div className="bg-white border border-brand/10 p-6">
          <ul className="space-y-3 text-[#2a2a2a]/85">
            {benefits.map((item) => (
              <li key={item} className="flex gap-3">
                <span className="text-brand">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h2
          className="text-3xl md:text-4xl text-brand mb-6 font-semibold"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Sample Description for Library Website/Brochure
        </h2>

        <div className="bg-white border border-brand/10 p-6">
          <p className="text-[#2a2a2a]/85 leading-relaxed">
            DELNET (Developing Library Network) is a premier library networking
            organization in India that facilitates resource sharing among
            member libraries. Through DELNET, users can access a vast collection
            of e-books, e-journals, theses, dissertations, and bibliographic
            databases. The network provides Inter-Library Loan (ILL) and
            Document Delivery Services, enabling users to obtain books and
            research articles from libraries across the country. DELNET
            significantly enhances the information resources available to
            students, faculty members, and researchers, supporting teaching,
            learning, and research activities.
          </p>

          <p className="text-sm text-[#3a3a3a]/70 mt-5">
            This version can be directly used in your college library webpage,
            NAAC report, library handbook, or user orientation programme.
          </p>
        </div>
      </section>
    </InnerLayout>
  );
};

export default DELNET;
